import {
    KubecostClient,
} from '../client';

import {
    ApiClientOptions,
} from '../types';

import {
    QuartermasterApi,
    quartermasterApiRef,
} from 'backstage-plugin-otk-ui';

import {
    CatalogApi,
    CatalogClient,
} from '@backstage/catalog-client';

import {
    CostInsightsApi,
    ProductInsightsOptions,
    Alert,
    DEFAULT_DATE_FORMAT,
} from '@backstage/plugin-cost-insights';

import {
    Cost,
    Entity,
    Group,
    MetricData,
    Project,
} from '@backstage/plugin-cost-insights-common';

import { DateTime, Duration, Interval } from 'luxon';

import {
    useApi,
} from '@backstage/core-plugin-api';

/**
 * Implements the cost insights api by pulling data from the kubecost api deployed in each k8s cluster,
 * and aggregating by otk teamspace.
 */
export class KubecostCostInsightsClient implements CostInsightsApi {
    private readonly catalogClient: CatalogApi;
    private readonly kubecostClient: KubecostClient;
    private readonly quartermasterApi: QuartermasterApi;

    constructor(opts: ApiClientOptions) {
        this.catalogClient = new CatalogClient(opts);
        this.kubecostClient = new KubecostClient(opts);
        this.quartermasterApi = useApi(quartermasterApiRef);
    }

    /**
     * Get the most current date for which billing data is complete, in YYYY-MM-DD format. This helps
     * define the intervals used in other API methods to avoid showing incomplete cost. The costs for
     * today, for example, will not be complete. This ideally comes from the cloud provider.
     */
    async getLastCompleteBillingDate(): Promise<string> {
        const lastUpdatedAt = await this.kubecostClient.fetchLastCompleteBillingDate();
        return DateTime.fromISO(lastUpdatedAt).toFormat(DEFAULT_DATE_FORMAT);
    }

    /**
     * Get a list of groups the given user belongs to. These may be LDAP groups or similar
     * organizational groups. Cost Insights is designed to show costs based on group membership;
     * if a user has multiple groups, they are able to switch between groups to see costs for each.
     *
     * This method should be removed once the Backstage identity plugin provides the same concept.
     *
     * @param userId - The login id for the current user
     */
    async getUserGroups(userId: string): Promise<Group[]> {
        const teamspaceEntities = await this.catalogClient.getEntities(
            { filter: { kind: ['System'], 'metadata.tags': ['teamspace'] } },
        );

        return teamspaceEntities.items.map(ts => {
            return { id: ts.metadata.name };
        });
    }

    /**
     * Get a list of cloud billing entities that belong to this group (projects in GCP, AWS has a
     * similar concept in billing accounts). These act as filters for the displayed costs, users can
     * choose whether they see all costs for a group, or those from a particular owned project.
     *
     * @param group - The group id from getUserGroups or query parameters
     */
    async getGroupProjects(group: string): Promise<Project[]> {
        const namespaces = await this.quartermasterApi.listTeamspaceNamespaces({ team: group });

        return namespaces.map((ns) => {
            return { id: ns.namespace };
        });
    }

    /**
     * Get daily cost aggregations for a given group and interval time frame.
     *
     * The return type includes an array of daily cost aggregations as well as statistics about the
     * change in cost over the intervals. Calculating these statistics requires us to bucket costs
     * into two or more time periods, hence a repeating interval format rather than just a start and
     * end date.
     *
     * The rate of change in this comparison allows teams to reason about their cost growth (or
     * reduction) and compare it to metrics important to the business.
     *
     * @param group - The group id from getUserGroups or query parameters
     * @param intervals - An ISO 8601 repeating interval string, such as R2/P30D/2020-09-01
     *   https://en.wikipedia.org/wiki/ISO_8601#Repeating_intervals
     */
    async getGroupDailyCost(group: string, intervals: string): Promise<Cost> {
        return this.kubecostClient.fetchDailyCostGroupReport(group, intervals);
    }

    /**
     * Get daily cost aggregations for a given billing entity (project in GCP, AWS has a similar
     * concept in billing accounts) and interval time frame.
     *
     * The return type includes an array of daily cost aggregations as well as statistics about the
     * change in cost over the intervals. Calculating these statistics requires us to bucket costs
     * into two or more time periods, hence a repeating interval format rather than just a start and
     * end date.
     *
     * The rate of change in this comparison allows teams to reason about the project's cost growth
     * (or reduction) and compare it to metrics important to the business.
     *
     * @param project - The project id from getGroupProjects or query parameters
     * @param intervals - An ISO 8601 repeating interval string, such as R2/P30D/2020-09-01
     *   https://en.wikipedia.org/wiki/ISO_8601#Repeating_intervals
     */
    async getProjectDailyCost(project: string, intervals: string): Promise<Cost> {
        return this.kubecostClient.fetchDailyCostProjectReport(project, intervals);
    }

    /**
     * Get cost aggregations for a particular cloud product and interval time frame. This includes
     * total cost for the product, as well as a breakdown of particular entities that incurred cost
     * in this product. The type of entity depends on the product - it may be deployed services,
     * storage buckets, managed database instances, etc.
     *
     * If project is supplied, this should only return product costs for the given billing entity
     * (project in GCP).
     *
     * The time period is supplied as a Duration rather than intervals, since this is always expected
     * to return data for two bucketed time period (e.g. month vs month, or quarter vs quarter).
     *
     * @param options - Options to use when fetching insights for a particular cloud product and
     *                interval time frame.
     */
    async getProductInsights(options: ProductInsightsOptions): Promise<Entity> {
        return this.kubecostClient.fetchProductInsightsReport(options.product, options.group, options.project, options.intervals);
    }

    /**
     * Get current cost alerts for a given group. These show up as Action Items for the group on the
     * Cost Insights page. Alerts may include cost-saving recommendations, such as infrastructure
     * migrations, or cost-related warnings, such as an unexpected billing anomaly.
     */
    async getAlerts(group: string): Promise<Alert[]> {
        // TODO: can define in, and pull from kubecost
        return [];
    }

    /**
     * Get aggregations for a particular metric and interval time frame. Teams
     * can see metrics important to their business in comparison to the growth
     * (or reduction) of a project or group's daily costs.
     *
     * @param metric - A metric from the cost-insights configuration in app-config.yaml.
     * @param intervals - An ISO 8601 repeating interval string, such as R2/P30D/2020-09-01
     *   https://en.wikipedia.org/wiki/ISO_8601#Repeating_intervals
     */
    async getDailyMetricData(metric: string, intervals: string): Promise<MetricData> {
        // TODO: unclear which metrics we'd be pulling in here?
        return {
            id: 'remove-me',
            format: 'number',
            aggregation: [],
            change: {
                ratio: 0,
                amount: 0
            }
        }
    }
}
